import type {SponsorCategory} from "sponsor/sponsorBooking";
import type {PageThemeTemplateKey} from "sponsor/sponsorOptions";
import type {PersonalizationFontKey} from "./personalizationFont";

/**
 * What `GET /transfer/personalization/offer` answers, and the rules that read it
 * (kitecorp/transferify-ui#556, api kitecorp/transferify-api-service#461).
 *
 * Plain TypeScript (this module imports no React): a rule living in a component would be a second place it
 * could be decided, and the second place is the one that goes stale.
 *
 * Since kitecorp/transferify#525 per-transfer personalization is included on every plan, the free plan too,
 * so the offer answers only the catalogue the builder composes from. Nothing here asks for a plan.
 */

/** What one treatment is, and how many images it needs and accepts, as the api publishes it. */
export interface PersonalizationTemplateArity {
	/**
	 * Which stage the treatment composes on, as the api derives it from the template
	 * (kitecorp/transferify-api-service#1149). This is what tells the builder which looks take the poster
	 * options, WITHOUT a second copy of the split: `sponsorOptions.ts` says which keys a treatment takes,
	 * and the catalogue says which treatments are on offer at all.
	 *
	 * Optional because an api that predates #1149 publishes no category — and because a treatment it
	 * never published cannot be chosen anyway, an absent category is simply a look with no options panel
	 * rather than a reason to hide the look.
	 */
	category?: SponsorCategory;
	minimumImages: number;
	maximumImages: number;
}

/** `PersonalizationOffer` on the wire. */
export interface PersonalizationOffer {
	/**
	 * The treatment catalogue with each entry's category and image arity — the api's rule, not a
	 * restatement of it. Keyed by what this bundle can DRAW rather than by the whole contract: the api
	 * never publishes a treatment it will not sell, and a name no effect here can animate would be a
	 * card whose preview is blank (kitecorp/transferify-api-service#1149).
	 */
	templates: Partial<Record<PageThemeTemplateKey, PersonalizationTemplateArity>>;
	/** The largest number of images any template accepts. */
	maxImages: number;
	/**
	 * The display faces a sender may choose, published for exactly the reason the template arity is
	 * (api#1099): a hard-coded copy in the composer would be a second list to keep in step, and the
	 * symptom of drift would be a picker offering a face the api answers 400 for. Read through
	 * {@link ./personalizationFont.offeredFonts}, which takes membership from here and order from there.
	 */
	fonts: readonly PersonalizationFontKey[];
}

/**
 * Whether the offer has answered (kitecorp/transferify#525).
 *
 * - `ready`: the offer arrived, so the builder has a catalogue to compose from. Every plan includes the
 *   look and the offer states the catalogue only, so nothing about the plan decides this.
 * - `pending`: the offer has not answered yet, and there is no catalogue to configure.
 */
export type PersonalizationAvailability = "ready" | "pending";

export function personalizationAvailability(offer: PersonalizationOffer | null): PersonalizationAvailability {
	return offer === null ? "pending" : "ready";
}

/**
 * What the composer may let this visitor DO (ui#764).
 *
 * `locked` is the signed-out rung. A signed-out visitor gets the whole composer to look at, with every
 * control inert. Nothing on that surface can compose: the images and logo are server-minted asset ids that
 * only an authenticated upload can produce (api#519 keeps personalization authenticated throughout), and
 * every template in the catalogue needs at least one image, so a signed-out draft could never be sendable.
 * Showing it locked advertises the feature honestly instead of hiding it behind a login modal.
 *
 * Every signed-in sender composes, whatever their plan, and sends what they composed: every plan includes
 * the look (kitecorp/transferify#525).
 */
export type PersonalizationAccess = "locked" | "composable";

/**
 * Whose offer and brand the composer may hold: the signed-in account, or nobody (`null`).
 *
 * Signing out does not reload the page, and the create flow that holds the composer stays mounted, so what
 * it read for one account would otherwise be what the next account in the same tab composes under: the
 * previous account's logo, colour and page theme. What the composer forgets when this answer changes is
 * {@link composerOnHolderChange}'s. The Firebase uid when the session carries one, else the email.
 */
export function personalizationOfferHolder(
	signedIn: boolean,
	user: {readonly id: string | null; readonly email: string},
): string | null {
	// The email is the fallback because a session saved before the uid was stored is identified by its email until the uid arrives.
	return signedIn ? user.id ?? user.email : null;
}

/** What the composer forgets when its holder changes, and the account it now remembers as the last one. */
export interface ComposerHolderChange {
	/** The offer and the inherited logo, colour and page theme: they were read for the previous holder. */
	forgetOffer: boolean;
	/** The composed look: its images were uploaded by the previous account. */
	discardDraft: boolean;
	/** The last account that was signed in here, which a sign-out does not clear. */
	lastAccount: string | null;
}

/**
 * What a change of {@link personalizationOfferHolder} makes the composer forget (ruling R9, kitecorp/transferify#525).
 *
 * The offer and the brand go on ANY change, a sign-out included, because they were read for someone who is not
 * signed in any more. The composed look goes only when a DIFFERENT account signs in: its images were uploaded
 * by the previous account and the api refuses them for anyone else, so keeping it would only produce a refused
 * send. A sign-out alone keeps it, because the same account may come back (a sign-out from another tab and the
 * same account signing in again, ui#1814), and its own look is still its own.
 */
export function composerOnHolderChange(input: {
	/** Whom the offer on hand was read for. */
	heldFor: string | null;
	/** Who is signed in now. */
	holder: string | null;
	/** The last account that was signed in here, kept across a sign-out. */
	lastAccount: string | null;
}): ComposerHolderChange {
	return {
		forgetOffer: input.heldFor !== input.holder,
		discardDraft: input.holder !== null && input.lastAccount !== null && input.lastAccount !== input.holder,
		lastAccount: input.holder ?? input.lastAccount,
	};
}

export function personalizationAccess(signedIn: boolean): PersonalizationAccess {
	return signedIn ? "composable" : "locked";
}

/**
 * What the builder's CONFIGURATION CONTROLS may do for this visitor.
 *
 * **The paywall is gone (ui#1814, owner decision 2026-09-01).** ui#1409 made the controls inert until a
 * one-off was paid — PAY FIRST, on the reasoning that a sender who owes it has bought the right to
 * compose. That is exactly the defect this ticket exists to remove: nobody buys a cosmetic they have
 * never seen, so the button asked for money before showing anything. Every signed-in sender now composes
 * the whole page and watches it as they go, and since kitecorp/transferify#525 there is no plan question at
 * all: every plan includes the look.
 *
 * Two rungs are left, and they answer different questions from different people:
 *
 * - `locked` — the signed-out preview (ui#764). Inert, pitched with `guestHint` and a way to log in.
 *   It stays because it is not a paywall: the images and logo are server-minted asset ids that only an
 *   authenticated upload can produce (api#519 keeps personalization authenticated throughout), so a
 *   signed-out draft could never be sendable. That boundary is the api's rule, not a pricing decision.
 * - `open` — everyone else. No dimming, no promo, nothing to buy before composing.
 */
export type PersonalizationComposerGate = "locked" | "open";

export function personalizationComposerGate(access: PersonalizationAccess): PersonalizationComposerGate {
	return access === "locked" ? "locked" : "open";
}

/**
 * Whether the builder renders its configuration sections at all.
 *
 * Only a signed-in sender whose offer is still `pending` answers no: the offer publishes the catalogue the
 * sections render from, and until it arrives there is nothing to configure. A `locked` visitor gets them
 * unconditionally; that preview is the whole point of ui#764, and it reads no offer to be blocked by.
 */
export function personalizationComposerShowsFields(
	gate: PersonalizationComposerGate,
	availability: PersonalizationAvailability,
): boolean {
	return gate === "locked" || availability !== "pending";
}

/**
 * Whether the controls ANSWER to this sender — editable, and a commit may arm the draft.
 *
 * The arming question is the same question as the editing one, and asking it here is what stops the
 * commit from arming a draft nobody could compose. A `locked` visitor and a sender whose offer is still
 * loading both have an unsendable draft on hand (no catalogue to check it against), so arming theirs
 * would turn the panel's send guard on against a sender with no control on screen to fix it with.
 */
export function personalizationComposerIsLive(
	gate: PersonalizationComposerGate,
	availability: PersonalizationAvailability,
): boolean {
	return gate === "open" && personalizationComposerShowsFields(gate, availability);
}

/**
 * Which actions the builder's FOOTER offers (ui#1418, re-read after ui#1814 removed the paywall).
 *
 * ui#1418 removed the DEAD ENDS: the footer used to render its primary commit unconditionally, which on
 * a rung with nothing to configure left a lone primary "Save" that saves nothing (such a rung is not
 * {@link personalizationComposerIsLive}, so the commit arms no draft and falls through to a plain close)
 * with no Cancel beside it — leaving the ✕, Escape and the outside click as the only ways out, none of
 * them in the footer. A signed-in sender whose offer is still loading is that rung today.
 *
 * The review of ui#1409 had also withheld the commit while the one-off was owed, so the paywall promo's
 * CTA was the single door onto that checkout. There is no promo and no in-builder checkout any more, so
 * that subtraction goes with them: the commit is once again exactly "there is something to commit".
 *
 * Answered HERE rather than in the sheet because it is one more reading of the same ladder, and a footer
 * assembled from conditions written in JSX is a second place that ladder gets decided. It is also the
 * only place the decision can be TESTED: this repo keeps no UI-component tests.
 */
export interface PersonalizationComposerFooter {
	/** The primary action: keeps the composed look, or — on the locked preview — plainly closes. */
	commit: boolean;
	/** The locked preview's way IN, beside its commit (ui#764). */
	signIn: boolean;
	/** The labelled way OUT, which drops the draft. */
	cancel: boolean;
}

export function personalizationComposerFooter(
	gate: PersonalizationComposerGate,
	availability: PersonalizationAvailability,
): PersonalizationComposerFooter {
	return {
		commit: personalizationComposerShowsFields(gate, availability),
		signIn: gate === "locked",
		// Every signed-in rung, including the ones with no commit (ui#1418). The locked preview keeps its
		// own pair unchanged: ui#764 pitches a visitor rather than offering them a way to abandon a draft
		// they were never able to compose.
		cancel: gate !== "locked",
	};
}

/**
 * The chip beside the look's heading (kitecorp/transferify#525): the look is free on every plan, so it reads
 * the same "Free" as the words' chip. `null` shows no chip.
 *
 * The locked preview gets none because it carries its own line (`guestIncludedNote`), and a signed-in sender
 * whose offer has not answered gets none while the builder has nothing to show.
 */
export function personalizationLookChip(
	gate: PersonalizationComposerGate,
	availability: PersonalizationAvailability,
): "free" | null {
	return gate === "open" && availability === "ready" ? "free" : null;
}

/**
 * The api-published arity for a template, or null when this offer does not carry it.
 *
 * A `null` template is a first-class input since ui#1814 — it is what a text-only personalization has —
 * and answers `null` for the same reason an unpublished template does: there is no arity to enforce.
 * Callers distinguish the two themselves; this one is the catalogue lookup, not the verdict.
 */
export function templateImageArity(
	offer: PersonalizationOffer | null,
	template: PageThemeTemplateKey | null,
): PersonalizationTemplateArity | null {
	return template === null ? null : offer?.templates[template] ?? null;
}

/** The templates this offer publishes, in the given display order. */
export function offeredTemplates(
	offer: PersonalizationOffer | null,
	order: readonly PageThemeTemplateKey[],
): readonly PageThemeTemplateKey[] {
	if (offer === null) {
		return [];
	}
	return order.filter(template => offer.templates[template] !== undefined);
}

/**
 * Whether the chosen look takes the poster settings at all (kitecorp/transferify-api-service#1149).
 *
 * Answered from the OFFER's category rather than from a local list of wallpaper names, so the panel
 * appears for exactly the looks the api says are wallpapers. A look the offer does not publish, and one
 * published by an api too old to carry a category, both answer `false` — the builder then shows the look
 * with no options panel, which is what every backdrop has always been, rather than a panel whose every
 * value the api would refuse.
 */
export function templateTakesOptions(
	offer: PersonalizationOffer | null,
	template: PageThemeTemplateKey | null,
): boolean {
	return templateImageArity(offer, template)?.category === "WALLPAPER";
}
